import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, extractApiError } from '../lib/api';
import { durationFromNow, fmtTime } from '../lib/format';
import { PhotoPicker } from '../components/PhotoPicker';
import type { CompressedPhoto } from '../lib/image';

interface Photo {
  id: string;
  phase: 'start' | 'end';
  seq: number;
  url: string;
}

interface ActiveDuty {
  id: string;
  locationName: string;
  startAt: string;
  note: string | null;
  latitude: number;
  longitude: number;
  dutyType: { name: string };
  photos: Photo[];
}

export function DutyActivePage() {
  const navigate = useNavigate();
  const [duty, setDuty] = useState<ActiveDuty | null>(null);
  const [endNote, setEndNote] = useState('');
  const [endPhotos, setEndPhotos] = useState<CompressedPhoto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, tick] = useState(0);

  async function reload() {
    try {
      const { data } = await api.get<ActiveDuty | null>('/app/duty-logs/active');
      if (!data) navigate('/', { replace: true });
      else setDuty(data);
    } catch {
      navigate('/', { replace: true });
    }
  }

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  async function endDuty() {
    if (!duty) return;
    setBusy(true);
    setError(null);
    try {
      // 1) ถ้ามีรูปตอนจบ → upload ก่อน
      if (endPhotos.length > 0) {
        const fd = new FormData();
        endPhotos.forEach((p, i) => fd.append('photos', p.blob, `end-${i}.jpg`));
        await api.post(`/app/duty-logs/${duty.id}/photos?phase=end`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // 2) ปิดงาน
      await api.patch(
        `/app/duty-logs/${duty.id}/end`,
        endNote.trim() ? { note: endNote.trim() } : {},
      );
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  async function cancelDuty() {
    if (!duty) return;
    if (!confirm('ยกเลิกบันทึกนี้? (จะตั้งเป็นสถานะ "ยกเลิก" และลบรูปที่อัปโหลดไว้)')) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/app/duty-logs/${duty.id}/cancel`, { reason: 'กดผิด' });
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  if (!duty) {
    return <div className="p-8 text-center text-gray-500">กำลังโหลด...</div>;
  }

  const startPhotos = duty.photos.filter((p) => p.phase === 'start');

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50">
      <header className="bg-line p-4 text-white">
        <p className="text-sm opacity-90">กำลังปฏิบัติงาน</p>
        <h1 className="text-xl font-bold">{duty.locationName}</h1>
        <p className="text-sm opacity-90">{duty.dutyType.name}</p>
      </header>

      <main className="space-y-4 p-4">
        <div className="card">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">เวลาเริ่ม</p>
              <p className="font-semibold">{fmtTime(duty.startAt)}</p>
            </div>
            <div>
              <p className="text-gray-500">ผ่านไป</p>
              <p className="font-semibold text-line">{durationFromNow(duty.startAt)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-500">พิกัด</p>
              <p className="font-mono text-xs">
                {duty.latitude.toFixed(5)}, {duty.longitude.toFixed(5)}
              </p>
            </div>
            {duty.note && (
              <div className="col-span-2">
                <p className="text-gray-500">หมายเหตุเริ่มงาน</p>
                <p>{duty.note}</p>
              </div>
            )}
          </div>
        </div>

        {startPhotos.length > 0 && (
          <div className="card">
            <p className="label">รูปตอนเริ่มงาน</p>
            <div className="grid grid-cols-2 gap-3">
              {startPhotos.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden rounded-2xl bg-gray-100"
                >
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <label className="label">รูปตอนจบงาน (ไม่บังคับ)</label>
          <PhotoPicker
            max={2}
            value={endPhotos}
            onChange={setEndPhotos}
            hint="ถ่ายภาพหลังจบงาน (ไม่บังคับ — สูงสุด 2 รูป)"
          />
        </div>

        <div className="card">
          <label className="label">หมายเหตุ (ตอนจบงาน)</label>
          <textarea
            className="textarea"
            rows={3}
            value={endNote}
            onChange={(e) => setEndNote(e.target.value)}
            placeholder="สรุปผล / เหตุการณ์ที่เกิด (ไม่จำเป็น)"
            maxLength={2000}
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button onClick={endDuty} disabled={busy} className="btn-primary">
          {busy
            ? 'กำลังบันทึก...'
            : endPhotos.length > 0
              ? `■ จบงานจุดนี้ (อัปโหลด ${endPhotos.length} รูป)`
              : '■ จบงานจุดนี้'}
        </button>
        <button onClick={cancelDuty} disabled={busy} className="btn-secondary text-red-600">
          ยกเลิกบันทึก
        </button>
      </main>
    </div>
  );
}
